import React, { useState } from 'react';
import { ResponsiveContainer, ComposedChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine, ReferenceArea, Area, Legend } from 'recharts';
import { INK, numFormat, pct, shortDate, addDays, axisProps, ChartTooltip, Section, StatTile, RangePicker } from '../analyticsUi';
import { COLORS, eur, eurFormat, eurRange, ageLabel } from './profitFormat';
import { UNASSIGNED_NETWORK } from '../budgetModel';
import Verdict from './Verdict';

const users = (v) => (v == null ? '–' : (Math.round(v * 10) / 10).toLocaleString('fi-FI'));
const signedUsers = (v) => `${v < 0 ? '−' : '+'}${users(Math.abs(v))}`;

const HISTORY_RANGES = [{ value: 90, label: '90 days' }, { value: 180, label: '6 months' }, { value: 365, label: '1 year' }, { value: 0, label: 'All' }];

const NETWORKS = {
  SEARCH: 'Google Search',
  SEARCH_PARTNERS: 'Search partners',
  CONTENT: 'Display network',
  YOUTUBE: 'YouTube',
  YOUTUBE_SEARCH: 'YouTube search',
  YOUTUBE_WATCH: 'YouTube videos',
  MIXED: 'Mixed (across networks)',
  [UNASSIGNED_NETWORK]: 'Not split by Google yet',
};
const networkLabel = (n) => NETWORKS[n] || 'Other (incl. Google Play)';
const strategyLabel = (s) => s.toLowerCase().replace(/_/g, ' ').replace(/^\w/, c => c.toUpperCase()).replace(/\b(cpa|roas|cpc|cpm)\b/g, a => a.toUpperCase());

function budgetFigures(budget, horizon, paidShare) {
  const lastStep = [...budget.steps].reverse().find(s => s.cost != null);
  let cost;
  if (budget.marginal && Number.isFinite(budget.marginal.value)) {
    cost = {
      label: 'Cost of one more user',
      value: eur(budget.marginal.value, true),
      note: budget.marginal.low != null ? `At today’s spend, likely ${eur(budget.marginal.low, true)}–${Number.isFinite(budget.marginal.high) ? eur(budget.marginal.high, true) : 'no effect'}` : 'At today’s spend',
    };
  } else if (lastStep) {
    cost = { label: 'Cost of one more user', value: eur(lastStep.cost, true), note: 'Last measurable step' };
  } else {
    cost = { label: 'Cost of one more user', value: 'Not measured', note: 'Needs a budget change to measure' };
  }
  return [
    {
      label: 'Daily budget now',
      value: budget.budgetNow != null ? eur(budget.budgetNow) : '–',
      note: [`Spending ${eur(budget.spendNow, true)} a day`, budget.usedShare != null ? `${pct(budget.usedShare)} used` : null].filter(Boolean).join(', '),
    },
    { label: 'Worth paying per extra user', value: `up to ${eur(budget.limit, true)}`, note: `A user earns ${eur(budget.value, true)} in their ${horizon}` },
    cost,
    budget.curve.adsShare?.range
      ? {
        label: 'New users from ads',
        value: pct(Math.max(0, budget.curve.adsShare.value)),
        note: `Likely ${pct(Math.max(0, budget.curve.adsShare.range.low))}–${pct(Math.min(1, budget.curve.adsShare.range.high))}; Google credits ${pct(paidShare)}`,
      }
      : { label: 'New users from ads', value: paidShare != null ? `≥ ${pct(paidShare)}` : '–', note: 'What Google credits' },
  ];
}

// The latest budget change while it runs: progress, users before and since, and what they cost so far
function BudgetTest({ test, limit }) {
  const { from, to } = test;
  const up = to.budget > from.budget;
  let status = 'info';
  let title;
  let text;
  if (!test.measuring) {
    title = 'Settling in';
    text = `Measuring starts ${shortDate(to.measuredFrom)}, once Google has adjusted.`;
  } else if (test.cost != null && test.costRange) {
    const { low, high } = test.costRange;
    const so = test.done ? '' : ' so far';
    // A raise pays when extra users cost less than they earn; a cut pays when the users given up cost more
    const pays = up ? high < limit : low > limit;
    const loses = up ? low > limit : high < limit;
    if (pays) {
      status = 'good';
      title = test.done ? (up ? 'The raise paid off' : 'The cut paid off') : 'Looking good so far';
    } else if (loses) {
      status = 'bad';
      title = test.done ? (up ? 'The extra users cost too much' : 'The cut lost valuable users') : 'Looking costly so far';
    } else {
      title = test.done ? 'Close to break-even' : 'Too close to call yet';
    }
    text = `${up ? 'Extra users cost' : 'Users given up saved'}${so} ${eurRange(low, high)} each; worth up to ${eur(limit, true)}.`;
  } else if (test.clear) {
    status = up ? 'bad' : 'good';
    title = up ? 'Fewer users despite more spend' : 'More users despite less spend';
    text = 'Something else probably changed too.';
  } else {
    status = test.done ? 'warn' : 'info';
    title = test.done ? 'No clear change' : 'No clear change yet';
    text = test.done ? 'The budget change bought little.' : 'Still within normal ups and downs.';
  }
  return (
    <Verdict
      heading={`Budget test: ${eur(from.budget)} → ${eur(to.budget)} since ${shortDate(to.from)}`}
      verdict={{
        status,
        title,
        text,
        figures: [
          {
            label: 'Progress',
            value: test.done ? 'Done' : `Day ${test.daysSoFar} of ${test.needed}`,
            note: test.done ? null : `Result by ${shortDate(addDays(to.from, test.needed - 1))}`,
          },
          {
            label: 'New users / day',
            value: test.measuring ? `${users(from.signupsPerDay)} → ${users(to.signupsPerDay)}` : users(from.signupsPerDay),
            note: test.measuring ? `Likely ${signedUsers(test.lowUsers)} to ${signedUsers(test.highUsers)}` : 'Before',
          },
          {
            label: 'Spend / day',
            value: test.measuring ? `${eur(from.spendPerDay, true)} → ${eur(to.spendPerDay, true)}` : eur(from.spendPerDay, true),
            note: test.measuring ? null : 'Before',
          },
        ],
      }}
    >
      {!test.done && (
        <div className="pf-progress" aria-hidden="true">
          <div className="pf-progress-bar" style={{ width: `${Math.min(100, (test.daysSoFar / test.needed) * 100)}%` }} />
        </div>
      )}
    </Verdict>
  );
}

function BudgetHistory({ budget }) {
  const [rangeDays, setRangeDays] = useState(180);
  const data = rangeDays ? budget.history.slice(-rangeDays) : budget.history;
  const rows = [...budget.periods].reverse();
  return (
    <Section
      title="Budget and new users"
      actions={<RangePicker options={HISTORY_RANGES} value={rangeDays} onChange={setRangeDays} />}
    >
      <div className="pf-chart">
        <ResponsiveContainer width="100%" height={200}>
          <ComposedChart data={data} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
            <CartesianGrid vertical={false} stroke={INK.grid} />
            <XAxis dataKey="date" tickFormatter={shortDate} minTickGap={40} {...axisProps} />
            <YAxis yAxisId="users" width={40} {...axisProps} axisLine={false} />
            <YAxis yAxisId="eur" orientation="right" width={56} tickFormatter={v => eurFormat.format(v)} {...axisProps} axisLine={false} />
            <Tooltip
              content={(
                <ChartTooltip
                  labelFormatter={shortDate}
                  valueFormatter={(v, p) => (p.dataKey === 'budget' || p.dataKey === 'spend' ? eur(v, true) : users(v))}
                />
              )}
            />
            <Legend iconType="plainline" wrapperStyle={{ fontSize: 12 }} />
            <Area yAxisId="eur" type="stepAfter" isAnimationActive={false} dataKey="budget" name="Daily budget" fill={COLORS.spend} fillOpacity={0.12} stroke={COLORS.spend} strokeOpacity={0.5} strokeWidth={1} connectNulls />
            <Line yAxisId="eur" isAnimationActive={false} dataKey="spend" name="Spend, 7-day avg" stroke={COLORS.spend} strokeWidth={1.5} strokeDasharray="4 3" dot={false} />
            <Line yAxisId="users" isAnimationActive={false} dataKey="signups" name="New users" legendType="none" stroke={COLORS.subs} strokeOpacity={0.3} strokeWidth={1} dot={false} />
            <Line yAxisId="users" isAnimationActive={false} dataKey="average" name="New users, 7-day avg" stroke={COLORS.subs} strokeWidth={2} dot={false} />
            <Line yAxisId="users" isAnimationActive={false} dataKey="credited" name="Credited installs, 7-day avg" stroke={COLORS.ads} strokeWidth={1.5} dot={false} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      {(rows.length > 0 || budget.current) && (
        <div className="pf-table-wrap">
          <table className="pf-table pf-table-fit">
            <thead><tr><th>{budget.fromBudgetHistory ? 'Period' : 'Between markers'}</th><th>Budget</th><th>Spend</th><th>Users</th><th>Credited</th><th>Extra user</th></tr></thead>
            <tbody>
              {budget.current && (
                <tr className="pf-row-muted">
                  <td>{shortDate(budget.current.from)} – now</td>
                  <td>{eur(budget.current.budget)}</td>
                  <td colSpan={4}>{budget.current.daysSoFar} {budget.current.daysSoFar === 1 ? 'day' : 'days'}, too new</td>
                </tr>
              )}
              {rows.map(p => {
                const step = budget.steps.find(s => s.to === p);
                let cost = '–';
                if (step) {
                  if (!step.measurable) cost = 'Unclear';
                  else if (step.cost != null) cost = eur(step.cost, true);
                  else cost = step.dSpend > 0 ? 'Fewer users' : 'More users';
                }
                return (
                  <tr key={p.from}>
                    <td>{shortDate(p.from)} – {p.to === budget.lastDay ? 'now' : shortDate(p.to)}{p.label ? ` · ${p.label}` : ''}</td>
                    <td>{p.budget != null ? eur(p.budget) : '–'}</td>
                    <td>{eur(p.spendPerDay, true)}</td>
                    <td>{users(p.signupsPerDay)}</td>
                    <td>{users(p.creditedPerDay)}</td>
                    <td className={step?.cost > budget.limit ? 'neg' : ''}>{cost}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <p className="pf-footnote">
        Per day. Extra user: what each extra user cost against the period before; red is over {eur(budget.limit, true)}.
      </p>
    </Section>
  );
}

// How long until a user has earned back what they cost, for the next user and on average
// (the earnings curve itself is on the Earnings tab)
function Payback({ payback, typical }) {
  if (!payback.length || !typical.length) return null;
  const lastAge = typical[typical.length - 1].age;
  return (
    <Section title="Payback">
      <div className="pf-metrics pf-metrics-flush">
        {payback.map(p => (
          <div key={p.key} className="pf-metric">
            <span className="pf-metric-label">{p.label}<span className="pf-metric-note">{eur(p.cost, true)}</span></span>
            <span className={`pf-metric-value ${p.age == null ? 'neg' : ''}`}>
              {p.age != null ? `${p.extrapolated ? '≈ ' : ''}${ageLabel(p.age)}` : `Over ${ageLabel(lastAge)}`}
            </span>
          </div>
        ))}
      </div>
    </Section>
  );
}

function ResponseCurve({ budget, className }) {
  return (
    <Section className={className} title="Budget response" hint="Profit per day from ads, by daily budget.">
      {budget.curve.fit ? (
        <div className="pf-chart">
          <ResponsiveContainer width="100%" height={260}>
            <ComposedChart data={budget.curve.chart} margin={{ top: 20, right: 16, bottom: 0, left: 0 }}>
              <CartesianGrid vertical={false} stroke={INK.grid} />
              <XAxis dataKey="budget" type="number" domain={[0, 'dataMax']} tickFormatter={v => eurFormat.format(v)} {...axisProps} />
              <YAxis tickFormatter={v => eurFormat.format(v)} width={64} {...axisProps} axisLine={false} />
              <Tooltip
                content={(
                  <ChartTooltip
                    labelFormatter={v => `${eur(v, true)} a day`}
                    valueFormatter={v => (Array.isArray(v) ? `${eur(v[0], true)} to ${eur(v[1], true)}` : eur(v, true))}
                  />
                )}
              />
              <ReferenceArea
                x1={budget.curve.tested.low}
                x2={budget.curve.tested.high}
                fill={INK.grid}
                fillOpacity={0.5}
                stroke="none"
                label={{ value: 'Budgets tried', position: 'insideBottomLeft', fill: INK.secondary, fontSize: 12 }}
              />
              <ReferenceLine y={0} stroke={INK.axis} />
              <ReferenceLine x={budget.budgetNow ?? budget.spendNow} stroke={COLORS.spend} strokeDasharray="4 4" label={{ value: 'Now', position: 'insideTopRight', fill: INK.secondary, fontSize: 12 }} />
              <Area isAnimationActive={false} dataKey="band" name="90% range" fill={COLORS.profit} fillOpacity={0.12} stroke="none" />
              <Line isAnimationActive={false} dataKey="profit" name="Expected profit per day" stroke={COLORS.profit} strokeWidth={2} dot={false} />
            </ComposedChart>
          </ResponsiveContainer>
          <p className="pf-footnote">
            From {budget.curve.weeks} weeks of data; beyond the budgets tried it’s a guess.
          </p>
        </div>
      ) : (
        <p className="pf-empty">No curve yet: {budget.curve.reason}.</p>
      )}
    </Section>
  );
}

// Google Ads' own numbers: how much the ads are shown, what that costs, and where the money goes
function GoogleAds({ ads, lostToBudget }) {
  const { now, prev, networks, networkShift, biddingStrategies, bidTarget } = ads;
  const totalSpend = networks.reduce((s, n) => s + n.spend, 0);
  const hint = [
    'Last 14 days against the 14 before.',
    biddingStrategies.length ? `Bidding: ${biddingStrategies.map(strategyLabel).join(', ')}.` : null,
  ].filter(Boolean).join(' ');
  return (
    <Section className="pf-span-all" title="Google Ads delivery" hint={hint}>
      {now ? (
        <div className="pf-tiles">
          <StatTile label="Impressions / day" value={now.impressions} previous={prev?.impressions} format={v => numFormat.format(Math.round(v))} period="14 days" />
          <StatTile label="Cost per 1000 impressions" value={now.cpm} previous={prev?.cpm} goodWhenUp={false} format={v => eur(v, true)} period="14 days" />
          <StatTile label="Click rate" value={now.ctr} previous={prev?.ctr} format={v => (v == null ? '–' : `${(v * 100).toLocaleString('fi-FI', { maximumFractionDigits: 1 })} %`)} period="14 days" />
          <StatTile
            label="Google conversions / day"
            value={now.conversions}
            previous={prev?.conversions}
            format={users}
            period="14 days"
            note={`Firebase: ${users(now.credited)}`}
          />
          <StatTile label="Cost per conversion" value={now.costPerConversion} previous={prev?.costPerConversion} goodWhenUp={false} format={v => eur(v, true)} period="14 days" />
          {bidTarget && (
            <StatTile label="Bid target" value={eur(bidTarget.target, true)} note={bidTarget.since ? `Since ${shortDate(bidTarget.since)}` : 'Per install'} />
          )}
          {/* Only Search-type campaigns report it, so App-only accounts never see this */}
          {lostToBudget != null && (
            <StatTile label="Lost to budget" value={pct(lostToBudget)} note="Search impressions" />
          )}
        </div>
      ) : (
        <p className="pf-empty">No delivery numbers yet.</p>
      )}
      {networks.length > 0 && totalSpend > 0 && (
        <div className="pf-table-wrap">
          <table className="pf-table pf-table-fit">
            <thead><tr><th>Network, last 30 days</th><th>Spend</th><th>Share</th><th>Impressions</th><th>Clicks</th><th>Conversions</th><th>From views</th><th>Cost per conversion</th></tr></thead>
            <tbody>
              {networks.filter(n => n.spend > 0 || n.conversions > 0).map(n => (
                <tr key={n.network}>
                  <td>{networkLabel(n.network)}</td>
                  <td>{eur(n.spend, true)}</td>
                  <td>{pct(n.spend / totalSpend)}</td>
                  <td>{n.impressions != null ? numFormat.format(n.impressions) : '–'}</td>
                  <td>{n.clicks != null ? numFormat.format(n.clicks) : '–'}</td>
                  <td>{users(n.conversions)}</td>
                  <td>{users(n.viewThrough)}</td>
                  <td>{n.conversions > 0 ? eur(n.spend / n.conversions, true) : '–'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="pf-footnote">Conversions include in-app purchases. From views: the user saw or watched an ad without clicking it, so the ad may deserve less of the credit.</p>
        </div>
      )}
      {networkShift && (
        <div className="pf-table-wrap">
          <table className="pf-table pf-table-fit">
            <thead><tr><th>Budget test, per day</th><th>Spend before</th><th>Spend now</th><th>Conversions before</th><th>Conversions now</th><th>Cost per extra conversion</th></tr></thead>
            <tbody>
              {networkShift.rows.map(r => (
                <tr key={r.network}>
                  <td>{networkLabel(r.network)}</td>
                  <td>{eur(r.spendBefore, true)}</td>
                  <td>{eur(r.spendAfter, true)}</td>
                  <td>{users(r.conversionsBefore)}</td>
                  <td>{users(r.conversionsAfter)}</td>
                  <td>{r.costPerExtra != null ? eur(r.costPerExtra, true) : '–'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="pf-footnote">Where the extra money went, from {networkShift.daysBefore} days before and {networkShift.daysAfter} days since the change that Google has fully split.</p>
        </div>
      )}
    </Section>
  );
}

export default function AdPlanning({ budget, typical, horizon, paidShare }) {
  if (!budget) return <p className="pf-empty">No budget advice yet: it needs Google Ads spend and a few weeks of new-user data.</p>;
  // Decision band first (the verdict, beside the running test if there is one), then what budgets
  // did next to what the model makes of them, then Google's own delivery numbers
  return (
    <div className="pf-ads-top">
      <Verdict
        className={budget.test ? '' : 'pf-span-all'}
        heading="What daily budget makes the most money?"
        verdict={{ status: budget.status, title: budget.title, text: budget.text, figures: budgetFigures(budget, horizon, paidShare) }}
      />
      {budget.test && <BudgetTest test={budget.test} limit={budget.limit} />}

      <BudgetHistory budget={budget} />
      <div className="pf-stack">
        <ResponseCurve budget={budget} />
        <Payback payback={budget.payback} typical={typical} />
      </div>

      {budget.ads && <GoogleAds ads={budget.ads} lostToBudget={budget.lostToBudget} />}
    </div>
  );
}
